import { z } from "zod"

import { QwenImageGenerationModelType } from "../../../../image/enums"
import { baseImageGenerationSchema } from "../helpers"

// Qwen Image schema (FAL: fal-ai/qwen-image)
export const qwenImageSchema = baseImageGenerationSchema.extend({
  modelId: z.literal(QwenImageGenerationModelType.QWEN_IMAGE),
  image_size: z
    .enum(["square_hd", "landscape_16_9", "landscape_4_3", "portrait_16_9", "portrait_4_3"])
    .default("landscape_4_3"),
  num_inference_steps: z.coerce.number().min(2).max(250).default(30),
  guidance_scale: z.coerce.number().min(0).max(20).default(2.5),
  seed: z.coerce.number().optional(),
  num_images: z.coerce.number().min(1).max(4).default(1),
  output_format: z.enum(["jpeg", "png"]).default("png"),
  negative_prompt: z.string().optional(),
})

export type QwenImageParams = z.infer<typeof qwenImageSchema>

// ====== Qwen Image 3 (FAL only — VM-1053) ======
//
// BILLING RULE (approved 2026-08-12, binding — do not widen).
// FAL publishes $0.04/image at 1K and $0.075/image at 2K, but its `image_size` accepts a
// free-form {width, height} anywhere from 512x512 to 2048x2048 total pixels and NEVER documents
// where 1K stops and 2K starts. A 1536x1536 request therefore has no known price.
//
// We resolve that by construction rather than by guessing: the exposed domain is a CLOSED enum
// whose every member sits unambiguously in one tier. All six of FAL's named presets are
// <= 1024x1024 (1K); the only 2K member is an explicit 2048x2048. Free-form dimensions are
// rejected here, so no reachable input can land in the unpriced 1024-2048 band.
//
// Widening this enum, or accepting {width, height}, reopens the blocker and needs a new decision.
const QWEN_3_SIZES_1K = [
  "square",
  "square_hd",
  "portrait_4_3",
  "portrait_16_9",
  "landscape_4_3",
  "landscape_16_9",
] as const

// 2K is TEXT-TO-IMAGE ONLY. FAL caps the edit endpoint at 1440x1440 total pixels, so 2048x2048
// is not a legal edit input — offering it there would produce provider rejections, not a 2K edit.
const QWEN_3_SIZE_2K = "2048x2048" as const

export const qwenImage3Schema = baseImageGenerationSchema
  .extend({
    modelId: z.literal(QwenImageGenerationModelType.QWEN_IMAGE_3),
    image_size: z.enum([...QWEN_3_SIZES_1K, QWEN_3_SIZE_2K]),
    negative_prompt: z.string().max(500).default(""),
    enable_prompt_expansion: z.boolean().default(true),
    seed: z.coerce.number().int().min(0).max(2147483647).optional(),
    output_format: z.enum(["jpeg", "png", "webp"]).default("png"),
  })
  .extend({ prompt: z.string().min(1).max(5000) })

// Edit: 1-3 reference images, order-significant (referenced in the prompt as "image 1/2/3").
//
// `image_size` is REQUIRED here even though FAL types it optional. When omitted FAL lets the model
// pick its own output resolution, which puts the generation in an unknown billing tier — exactly
// the ambiguity the closed enum above exists to remove. Requiring it keeps the cost exact.
export const qwenImage3EditSchema = baseImageGenerationSchema
  .extend({
    modelId: z.literal(QwenImageGenerationModelType.QWEN_IMAGE_3_EDIT),
    image_urls: z.array(z.string()).min(1).max(3).describe("1-3 reference images; order matters"),
    image_size: z.enum(QWEN_3_SIZES_1K),
    negative_prompt: z.string().max(500).default(""),
    enable_prompt_expansion: z.boolean().default(true),
    seed: z.coerce.number().int().min(0).max(2147483647).optional(),
    output_format: z.enum(["jpeg", "png", "webp"]).default("png"),
  })
  .extend({ prompt: z.string().min(1).max(5000) })

// NOTE: `num_images` (FAL allows 1-4) is deliberately NOT exposed, same as Grok Imagine 2.0.
// VidMax's production image path is async — `helpers.ts` calls `runner.submit`, and
// `falImageRunner.submit` hardcodes `buildFalInput(params, 1)` — so exactly one image is ever
// requested. The control would multiply the bill without changing the output.

/** Every 1K-tier `image_size` value, for pricing to test membership against. */
export const QWEN_IMAGE_3_1K_SIZES: readonly string[] = QWEN_3_SIZES_1K
export const QWEN_IMAGE_3_2K_SIZE: string = QWEN_3_SIZE_2K

export type QwenImage3Params = z.infer<typeof qwenImage3Schema>
export type QwenImage3EditParams = z.infer<typeof qwenImage3EditSchema>
